/**
 * The route-level counterpart to `<PermissionCheck>`: same `oneOf` vocabulary,
 * evaluated in a loader so a typed URL is gated like a rendered control. Both go
 * through `holdsPermissions`, so they cannot drift.
 *
 * ```ts
 * { path: 'import', loader: requirePermissions({ oneOf: PagePermissions.settingsImport }) }
 * { path: 'items/:id', loader: requirePermissions({ oneOf: [Permissions.items.read] }, itemLoader) }
 * ```
 */
import {
  redirect,
  type LoaderFunction,
  type LoaderFunctionArgs,
  type Params,
} from 'react-router-dom';

import { getMyPermissionsQueryOptions } from '@/api/getMyPermissions';
import { queryClient } from '@/lib/react-query';
import {
  holdsPermissions,
  type PermissionCheckOptions,
} from '@/models/permission';

// Warmed by the game route's loader, so this is normally a cache read.
//
// A read failure is deliberately NOT swallowed: an unreadable permission list is not
// a denial, and the error page is the honest outcome. On an expired token the root
// authLoader's redirect to /login wins anyway.
const myGrants = async () => {
  const { roles } = await queryClient.ensureQueryData(
    getMyPermissionsQueryOptions(),
  );

  return roles;
};

// A thrown Response — not an Error — is React Router's contract for a status the
// error element should render: only that makes `isRouteErrorResponse` true, which is
// how ErrorPage tells 403 from 404 from a crash. Hence the rule waivers below.
//
// The body carries what was required, so the error page can name it.
export const forbidden = (check: PermissionCheckOptions) =>
  new Response(JSON.stringify({ required: check }), {
    status: 403,
    statusText: 'Forbidden',
    headers: { 'Content-Type': 'application/json' },
  });

/**
 * A 403 for a Studio or Game outside the viewer's reach: no code to name, what is
 * missing is the scope itself.
 */
export const outOfScope = () => forbidden({ oneOf: [] });

/**
 * Denies the route with a 403 unless the viewer holds `check` on the current scope,
 * then defers to `loader` when the route has one of its own.
 */
export const requirePermissions =
  (check: PermissionCheckOptions, loader?: LoaderFunction): LoaderFunction =>
  async (args) => {
    const grants = await myGrants();

    if (!holdsPermissions(grants, check, args.params))
      // eslint-disable-next-line @typescript-eslint/only-throw-error -- see forbidden()
      throw forbidden(check);

    return loader ? loader(args) : null;
  };

/**
 * The loader-side answer to `usePermissionCheck`, for a loader deciding whether to
 * *fetch* something rather than whether to deny the route — warming a cache the
 * viewer may not be allowed to fill would just 403. A loader cannot call a hook, but
 * it can read the same Grants.
 */
export const holdsPermissionsInLoader = async (
  check: PermissionCheckOptions,
  params: Params,
) => holdsPermissions(await myGrants(), check, params);

type Candidate = { to: string } & PermissionCheckOptions;

/**
 * For a group's index route: redirects to the first candidate the viewer can open
 * instead of always to the first one. `to` is relative to the group, like the plain
 * `redirect('general')` it replaces.
 *
 * With no candidate left, 403 — unless a `fallback` is given, which is for
 * destinations the app picks on the user's behalf rather than ones they asked for.
 */
export const firstAllowed =
  (
    candidates: Candidate[],
    fallback?: (params: Params) => string,
  ): LoaderFunction =>
  async ({ request, params }: LoaderFunctionArgs) => {
    const grants = await myGrants();
    const allowed = candidates.find((candidate) =>
      holdsPermissions(grants, candidate, params),
    );

    if (!allowed) {
      if (fallback) return redirect(fallback(params));

      // Report every code in the group: no single candidate is "the" missing one.
      // eslint-disable-next-line @typescript-eslint/only-throw-error -- see forbidden()
      throw forbidden({
        oneOf: candidates.flatMap(({ oneOf }) => oneOf),
      });
    }

    // Resolved against the group's own URL with a trailing slash, so `to` is appended
    // to the group rather than replacing its last segment.
    const base = new URL(request.url);
    base.pathname = `${base.pathname.replace(/\/+$/, '')}/`;

    return redirect(new URL(allowed.to, base).pathname);
  };
